'use client';

import { cva, VariantProps } from 'class-variance-authority';
import { OctagonAlertIcon, ThumbsUpIcon, XIcon } from 'lucide-react';
import { ComponentProps, ReactNode } from 'react';
import {
    toast as sonnerToast,
    Toaster as Sonner,
    type ToasterProps,
} from 'sonner';

export function Toaster(props: Omit<ToasterProps, 'toastOptions'>) {
    return (
        <Sonner
            className="group/toaster"
            position="bottom-right"
            offset={16}
            {...props}
            style={{
                fontFamily: 'inherit',
                ...props.style,
            }}
            toastOptions={{
                unstyled: true,
            }}
        />
    );
}

const toastVariants = cva(
    'flex w-full items-center rounded-md border px-4 py-3 shadow-md md:w-[356px]',
    {
        variants: {
            variant: {
                info: 'bg-popover text-popover-foreground',
                error: 'bg-popover text-popover-foreground',
                success: 'bg-popover text-popover-foreground',
            },
        },
    }
);

function Toast({
    className,
    variant,
    title,
    description,
    id,
    ...props
}: Omit<ComponentProps<'div'>, 'id' | 'title' | 'description'> &
    VariantProps<typeof toastVariants> & {
        id: string | number;
        title: string;
        description?: ReactNode;
    }) {
    return (
        <div
            className={toastVariants({ className, variant })}
            {...props}
        >
            <div
                data-slot="toast-icon"
                className="mr-4 flex-none"
            >
                {variant === 'error' && <OctagonAlertIcon className="size-5" />}
                {variant === 'success' && <ThumbsUpIcon className="size-5" />}
            </div>
            <div
                data-slot="toast-content"
                className="flex grow flex-col"
            >
                <p
                    data-slot="toast-title"
                    className="text-sm font-medium"
                >
                    {title}
                </p>
                {description && typeof description === 'string' && (
                    <p
                        data-slot="toast-description"
                        className="text-muted-foreground mt-1 text-xs"
                    >
                        {description}
                    </p>
                )}

                {description && typeof description !== 'string' && (
                    <div
                        data-slot="toast-description"
                        className="text-muted-foreground mt-1 text-xs"
                    >
                        {description}
                    </div>
                )}
            </div>
            <div
                data-slot="toast-close"
                className="absolute top-3 right-4 flex h-5 w-4 items-center justify-center"
            >
                <button
                    data-slot="toast-close"
                    className="ring-offset-background focus:ring-ring flex items-center justify-center rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
                    onClick={() => {
                        sonnerToast.dismiss(id);
                    }}
                >
                    <XIcon className="pointer-events-none size-4 shrink-0" />
                    <span className="sr-only">Close</span>
                </button>
            </div>
        </div>
    );
}

type Position =
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-center'
    | 'bottom-center';

export function toast(
    variant: VariantProps<typeof toastVariants>['variant'],
    title: string,
    props?: {
        dismissible?: boolean;
        duration?: number;
        description?: ReactNode;
        position?: Position;
    }
) {
    return sonnerToast.custom(
        (id) => (
            <Toast
                {...props}
                id={id}
                title={title}
                variant={variant}
            />
        ),
        {
            dismissible: props?.dismissible,
            duration: props?.duration,
            position: props?.position,
        }
    );
}
